import React from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Route-level guard for /admin/venues/:venueId/* — redirects a VENUE_USER who
 * tries to reach a venue they aren't assigned to. Backend already enforces
 * this with venueAccessMiddleware (returns 403); this guard exists only to
 * give a clean UX instead of letting the page render and emit a chain of
 * failed API calls.
 *
 * SYSTEM_ADMIN / ADMIN always pass through.
 *
 * VENUE_USER:
 *   - assigned to :venueId → pass through
 *   - not assigned        → redirect:
 *       0 venues → /unauthorized
 *       1 venue  → /admin/venues/<that one>
 *       2+       → /admin/my-venues
 *
 * The redirect target is "the place this user actually has access to,"
 * not just a 403 page — feels less broken than bouncing them to an error
 * screen for what is really just a wrong URL.
 */
const VenueAccessGuard: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const { user, isAdmin, isVenueUser, canAccessVenue } = useAuth();

  if (isAdmin()) return <Outlet />;

  if (isVenueUser()) {
    if (venueId && canAccessVenue(venueId)) return <Outlet />;

    const assigned = user?.assignedVenueIds ?? [];
    if (assigned.length === 0) return <Navigate to="/unauthorized" replace />;
    if (assigned.length === 1) return <Navigate to={`/admin/venues/${assigned[0]}`} replace />;
    return <Navigate to="/admin/my-venues" replace />;
  }

  return <Navigate to="/unauthorized" replace />;
};

export default VenueAccessGuard;
